/* The narrow index on the left of `/zadania` — 320 wide, ending on the card's bottom edge.
 *
 * Canon: `zadania-c-runda5.html`, "C, round 3". Its own `.queue` is `align-self: start` with a
 * height derived from the desk; here the grid row already bounds both columns, so the card is the
 * one that states the height and this column simply fills it. Same outcome, one fewer magic number.
 *
 * The rows never move: a handled row is struck through IN PLACE (see `model/taskDeck`), so the list
 * has the same length and the same order from the first answer to the last. */

.queue {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl);
}

.head {
  position: relative;
  flex: none;

  /* The canon's `.queue .qh`: 18px around, 14px under. `--space-5` IS 18px. */
  padding: var(--space-5) var(--space-5) var(--space-14px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headTitle {
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

/* The figure sits in a fixed track to the left of the `⋯`, so a count that changes from 0 to 14
 * when the read lands cannot move the title beside it. */
.headCount {
  position: absolute;
  top: 20px;
  right: 58px;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.more {
  position: absolute;
  top: 13px;
  right: var(--space-4);
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.more:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

.more:hover {
  color: var(--text);
  background: var(--bg-inset);
}

.moreGlyph {
  width: 16px;
  height: 16px;
}

/* The progress meter. A `<div>` with `role="progressbar"` rather than a `<progress>`: the native
 * element cannot be styled to the canon's 6px bar in every engine and brings a default appearance
 * that differs per platform. The figure above it says the same thing in words. */
.meter {
  height: 6px;

  /* The canon's own 10px between the title and the bar. */
  margin-top: var(--space-10px);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-hair);
}

.meterFill {
  height: 100%;
  background: var(--text);
  border-radius: var(--radius-hair);
}

/* ── The scope switch ──────────────────────────────────────────────────────────────────────── */

/* A RADIO GROUP, not a tablist — `pages/plan-library/ui/ScopeSwitch` states the rule and this
 * follows it: arrows move the selection inside a radio group, which is the platform contract, and
 * `useTaskShortcuts` is inert inside one for exactly that reason. */
.scope {
  display: flex;
  gap: var(--space-1px);
  min-inline-size: var(--space-0);
  margin: var(--space-3) var(--space-0) var(--space-0);
  padding: var(--space-3px);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.scopeOption {
  /* The gap is SIX pixels, the mock's own, and it is the whole of the "Zadania11" the owner
   * reported: at 1.5px the count touched the word and the two read as one token. It is a gap, not a
   * missing space — the string carries one and always did. */
  display: inline-flex;
  flex: 1;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  height: 28px;

  /* `.scope button` is 12.5px in the canon; `--fs-note` is the repo's 12.5. */
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  border-radius: var(--radius-9-5px);
}

.scopeSelected {
  color: var(--text);
  background: var(--bg-card);
  box-shadow: var(--shadow-hover);
}

/* The radio itself is clipped rather than hidden: `display: none` takes it out of the tab order and
 * out of the accessibility tree, and the label's own box is then a control nothing can reach. */
.scopeInput {
  position: absolute;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.scopeWord {
  white-space: nowrap;
}

.scopeInput:focus-visible + .scopeWord {
  border-radius: var(--radius-hair);
  box-shadow: var(--ring-menu);
}

.scopeCount {
  font-variant-numeric: tabular-nums;
  opacity: 0.7;
}

/* ── The rows ──────────────────────────────────────────────────────────────────────────────── */

.list {
  flex: 1;
  min-height: 0;
  padding: var(--space-0);
  margin: var(--space-0);
  overflow-y: auto;
  list-style: none;
}

.row {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  width: 100%;
  padding: var(--space-9px) var(--space-4);
  text-align: left;
  background: none;
  border: 0;
  border-top: var(--border-hairline) solid var(--border);
}

.row:hover {
  background: var(--bg-inset);
}

/* The selected row. An inset ink rule at the leading edge plus the sunken fill — two channels, so
 * the selection survives a greyscale screen and a colour-blind reader. `--ring-lifted` is the
 * repo's own 3px ink rule, the same one the plan library's chosen row wears. */
.rowSelected {
  background: var(--bg-inset);
  box-shadow: var(--ring-lifted);
}

/* The index row is a square full-bleed row, so its ring is the INSET ink one rather than an
 * outline: rounding the row to give an outline something to follow would round the hover fill with
 * it, and the mock's `.queue .row` runs edge to edge between hairlines. A selected row keeps its
 * leading rule as well — the two say different things (where the keyboard is, which card is on the
 * desk) and must be able to say them at once. */
.row:focus-visible {
  outline: var(--border-focus) solid var(--text);

  /* `--space-minus-2px` is the negative of `--border-focus` (2px): the ring lands just inside
   * the row instead of straddling the hairline above it. */
  outline-offset: var(--space-minus-2px);
}

.list li:first-child .row {
  border-top: 0;
}

/* `ReasonTile` at the index's own 28px and 8px corner. Scoped under the row so it outranks the
 * tile's size class by specificity rather than by which stylesheet chunk loads last. */
.row .tile {
  --tile-size: 28px;
  --mark-size: 13px;

  border-radius: var(--radius-8px);
}

/* The mark's own box inside the tile, so `markIn`'s scale has something to scale that is not the
 * tile's own ground. */
.tileSwap {
  display: grid;
  place-items: center;
}

.tileMark {
  width: 13px;
  height: 13px;
}

 /* `min-width: 0` is what lets the two lines ellipsise instead of pushing the age off the row: a
  * flex item's default `min-width: auto` refuses to shrink below its content, so the long reason
  * ran under the date. */
.rowText {
  flex: 1;
  min-width: 0;
}

/* BLOCK, not the default inline. As spans they shared one line box — "Marek NowakOpuszczony
 * trening" — and `text-overflow` does nothing to an inline box, so nothing clipped either. */
.name {
  display: block;
  overflow: hidden;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-name);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.reason {
  display: block;
  margin-top: var(--space-1px);
  overflow: hidden;
  font-size: var(--fs-label);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-2);
}

.age {
  flex: none;
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* A row with an answer on it. The strike-through is the SECOND channel beside the tile's mark and
 * the ink: `--text-3` alone would encode it in colour, and this is a state a reader has to be able
 * to tell. The row stays where it is — that is the whole design. */
.rowHandled .name,
.rowHandled .reason {
  color: var(--text-3);
  text-decoration: line-through;
}

.rowHandled .tile {
  color: var(--text-3);
  background: var(--bg-chip);
}

/* ── The foot ──────────────────────────────────────────────────────────────────────────────── */

/* Only drawn while a decision is held, which is the only time there is anything to undo. It carries
 * its own top rule rather than a reserved height: it stands at the BOTTOM of a column the card's
 * height fixes, so it grows into the list's scroller and moves nothing on the page. */
.foot {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: flex-end;
  padding: var(--space-2) var(--space-10px);
  border-top: var(--border-hairline) solid var(--border);
}

.undo {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
}

.undoGlyph {
  width: 14px;
  height: 14px;
}

/* ── The loading reserve ───────────────────────────────────────────────────────────────────── */

.skeletonRow {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-9px) var(--space-4);
  border-top: var(--border-hairline) solid var(--border);
}

.barTile {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-8px);
}

.barName {
  display: block;
  width: 118px;
  height: 13px;
  border-radius: var(--radius-hair);
}

.barReason {
  display: block;
  width: 84px;
  height: 11px;
  margin-top: var(--space-10px);
  border-radius: var(--radius-hair);
}

/* The `Z` on the undo control, the same key treatment the page foot's legend draws. */
.key {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

/* ── The chat glyph beside a row (chat canon, board P1) ────────────────────────────────────────
 * A sibling of the row's button, laid over its trailing edge: shown on hover, on the selected row,
 * on keyboard focus, and held lit on the row whose conversation the panel is showing. The row keeps
 * room for it so the age is never under it. */
.rowItem {
  --row-chat-shown: 0;
  --row-chat-ink: var(--text-3);
  --row-chat-fill: transparent;

  position: relative;
}

.rowItem:hover,
.rowItem:focus-within,
.rowItemSelected {
  --row-chat-shown: 1;
}

.rowItemTalking {
  --row-chat-shown: 1;
  --row-chat-ink: var(--text);
  --row-chat-fill: var(--bg-chip);
}

.rowWithChat {
  padding-right: var(--space-12);
}

.rowChat {
  position: absolute;
  top: 50%;
  right: var(--space-2);
  translate: 0 -50%;
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: var(--row-chat-ink);
  background: var(--row-chat-fill);
  border: 0;
  border-radius: var(--radius-mark);
  cursor: pointer;
  opacity: var(--row-chat-shown);
  transition: opacity var(--dur-fast);
}

.rowChat:hover {
  color: var(--text);
  background: var(--bg-chip);
}

.rowChat:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}
